import { useForm, useWatch, type UseFormReturn } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { useEffect, useState } from "react";
import "./style.css";
import "./index.css";
import neomLogImage from "./assets/neomLogo.png";
import { ThemeProvider, useTheme } from "./hooks/theme";
import { LocaleProvider, useLocale } from "./hooks/locale";
import { isDev } from "./constants/env";
import { ApiProvider, useApi } from "./hooks/api";
import { ThemedView } from "./components/ThemedView";
import ThemeAndLocale from "./components/ThemeAndLocale";
import { Card } from "./components/Card";
import { FormTextField } from "./components/Form/FormTextField";
import { FormOptions } from "./components/Form/FormOptions";
import { AlertView } from "./components/AlertView";
import { extractErrorMessage } from "./lib/error";
import {
    nationalIdRegex,
    passportRegex,
    phoneRegex,
} from "./constants/validation";
import { ScreenModeProvider, useScreenMode } from "./hooks/display";
import { Spinner } from "@/components/ui/spinner"
import { sleep } from "./lib/utils";

import SuccessIcon from "@/assets/icons/success.svg"

const deletionFormSchema = z
    .object({
        firstName: z.string().min(2, {
            message: "Invalid firstname",
        }),
        lastName: z.string().min(2, {
            message: "Invalid lastname",
        }),
        identityType: z.enum(["passport", "nationalId"]),
        identityNumber: z.string().min(5),
        phoneNumber: z
            .string()
            .regex(phoneRegex, {
                error: "Phone Invalid",
            })
            .transform((val) => {
                const match = val.match(phoneRegex)!;
                return match[1]!;
            }),
        reason: z.string(),
    })
    .superRefine((data, ctx) => {
        if (data.identityType === "passport") {
            if (!passportRegex.test(data.identityNumber)) {
                ctx.addIssue({
                    code: "custom",
                    path: ["identityNumber"],
                    message: "Invalid passport number",
                });
            }
        }

        if (data.identityType === "nationalId") {
            if (!nationalIdRegex.test(data.identityNumber)) {
                ctx.addIssue({
                    code: "custom",
                    path: ["identityNumber"],
                    message: "Invalid national ID",
                });
            }
        }
    });

type DeletionForm = z.infer<typeof deletionFormSchema>;

const DeletionForm = () => {
    const [loading, setLoading] = useState(false);
    const { t } = useLocale();
    const [errorMessage, setErrorMessage] = useState(null as null | string);
    const [success, setSuccess] = useState(false);
    const { api } = useApi();
    const form = useForm<DeletionForm>({
        mode: "onChange",
        resolver: zodResolver(deletionFormSchema),
        defaultValues: {
            firstName: "",
            lastName: "",
            identityType: "nationalId",
            identityNumber: "",
            phoneNumber: "",
            reason: "",
        },
    });
    const theme = useTheme();
    const submit = async (form: DeletionForm) => {
        if (loading) {
            return;
        }
        setLoading(true);
        setErrorMessage(null);
        try {
            if(isDev){
                await sleep(3e3)
            }

            await api.post("/api/public/delete-profile-request", form);
            setSuccess(true);
        } catch (error) {
            console.error(error);
            const errorMessage = extractErrorMessage(error);
            setErrorMessage(errorMessage);
        } finally {
            setLoading(false);
        }
    };
    const idType = useWatch({
        control: form.control,
        name: "identityType",
    });

    if(success){
        return <ThemedView
            style={{
                alignItems: "center", 
                justifyContent: "center", 
                gap: "22px", 
                flex: 1, 
            }}
        >
            <ThemedView>
                <img
                    src={SuccessIcon}
                    style={{
                        height: "120px", 
                        objectFit: "contain", 
                    }}
                ></img>
            </ThemedView>
            <ThemedView
                style={{
                    fontSize: "22px", 
                    fontWeight: "600", 
                    textAlign: "center", 
                    color: theme.colors.onBackground, 
                }}
            >{t("Success!! your request has been sent, we will be in contact as soon as possible")}</ThemedView>
        </ThemedView>


    }

    return (
        <>
            <Card>
                <Form {...form}>
                    <form onSubmit={form.handleSubmit(submit)}>
                        <ThemedView
                            style={{
                                gap: "12px",
                                padding: "22px",
                            }}
                        >
                            <FormTextField
                                label={"First Name"}
                                form={form}
                                name="firstName"
                            ></FormTextField>
                            <FormTextField
                                label={"Last Name"}
                                form={form}
                                name="lastName"
                            ></FormTextField>
                            <FormOptions
                                name="identityType"
                                form={form}
                                label="Identity Type"
                                options={[
                                    {
                                        value: "passport",
                                        label: "Passport",
                                    },
                                    {
                                        value: "nationalId",
                                        label: "national Id",
                                    },
                                ]}
                            ></FormOptions>
                            <FormTextField
                                label={
                                    idType == "passport"
                                        ? "Passport Number"
                                        : "National Id"
                                }
                                form={form}
                                name="identityNumber"
                            ></FormTextField>
                            <FormTextField
                                label={"Phone Number"}
                                name="phoneNumber"
                                form={form}
                            ></FormTextField>{" "}
                            <FormTextField
                                label={"Reason"}
                                name="reason"
                                form={form}
                            ></FormTextField>
                            <Button
                                loading={loading}
                                style={{
                                    background: theme.colors.primary,
                                    color: theme.colors.onPrimary,
                                }}
                                disabled={loading}
                            >
                                {t("Submit")}
                            </Button>
                            <AlertView
                                type="error"
                                onClear={() => {
                                    setErrorMessage(null);
                                }}
                                message={errorMessage}
                            ></AlertView>
                        </ThemedView>
                    </form>
                </Form>
            </Card>
        </>
    );
};

const NeomLogo = () => {
    return (
        <img
            src={neomLogImage}
            style={{
                objectFit: "contain",
                height: "35px",
            }}
        ></img>
    );
};
const Header = () => {
    const { isMobile } = useScreenMode();
    return (
        <ThemedView
            style={{
                flexDirection: isMobile ? "column" : "row",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "22px",
                padding: "12px 22px",
            }}
        >
            <NeomLogo></NeomLogo>
            <ThemeAndLocale></ThemeAndLocale>
        </ThemedView>
    );
};

const Main = () => {
    const theme = useTheme();
    const locale = useLocale();
    return (
        <>
            <ThemedView
                style={{
                    flex: 1,
                    direction: locale.localeDirection,
                    background: theme.colors.background,
                    color: theme.colors.onBackground,
                }}
            >
                <ThemedView
                    style={{
                        flex: 1,
                        alignItems: "center",
                    }}
                >
                    <ThemedView
                        style={{
                            maxWidth: "720px",
                            width: "100%",
                            padding: "22px",
                            gap: "22px",
                            flex: 1, 
                        }}
                    >
                        <Header></Header>
                        <ThemedView
                            style={{
                                flex: 1,
                                justifyContent: "center",
                            }}
                        >
                            <DeletionForm />
                        </ThemedView>
                    </ThemedView>
                </ThemedView>
            </ThemedView>
        </>
    );
};

export const App = () => {
    useEffect(() => {
        console.log("Env", { isDev });
    }, []);
    return (
        <>
            <ScreenModeProvider>
                <ApiProvider>
                    <ThemeProvider>
                        <LocaleProvider>
                            <Main />
                        </LocaleProvider>
                    </ThemeProvider>
                </ApiProvider>
            </ScreenModeProvider>
        </>
    );
};
